import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { fmtZMW, canSeeSalary, exportCSV } from "@/lib/helpers";
import { useAuth } from "@/contexts/AuthContext";
import {
  PageHeader, MetricCard, Empty, BtnGhost, BtnPrimary, BtnSecondary, Modal, Field, Input, Select,
} from "@/components/primitives";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { DownloadSimple, Printer, Play, PencilSimple, CheckCircle, Files, ArrowsClockwise, Trash } from "@phosphor-icons/react";

async function openBlobPdf(url) {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const objUrl = URL.createObjectURL(data);
    window.open(objUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
  } catch (e) {
    toast.error(formatError(e));
  }
}

// ------------------------------------------------------------
// Client-side clone of the Phizo auto payroll math (mirrors the backend).
// Keeping it here means the edit modal recomputes live as the user types.
// ------------------------------------------------------------
const PAYE_BANDS = [
  [5100, 0.00],
  [7100, 0.20],
  [9200, 0.30],
  [Infinity, 0.37],
];
function zmwPaye(taxable) {
  if (taxable <= 0) return 0;
  let tax = 0, prev = 0;
  for (const [ceil, rate] of PAYE_BANDS) {
    const band = Math.min(taxable, ceil) - prev;
    if (band <= 0) break;
    tax += band * rate;
    prev = ceil;
    if (taxable <= ceil) break;
  }
  return Math.round(tax * 100) / 100;
}
function autoPayrollMath(basic, { paye, napsa, nhima, allowances = 0, salaryAdvance = 0, gratuityPct = 0.25, exemptFromNapsa = false } = {}) {
  const b = Math.round((Number(basic) || 0) * 100) / 100;
  const adv = Math.round((Number(salaryAdvance) || 0) * 100) / 100;
  if (b <= 0) {
    return {
      basic_zmw: 0, gross_zmw: 0, taxable_gross_zmw: 0,
      napsa_zmw: 0, nhima_zmw: 0, paye_zmw: 0,
      salary_advance_zmw: adv,
      net_pay_zmw: -adv, gratuity_25_zmw: 0,
      net_with_gratuity_zmw: -adv,
    };
  }
  const napsaCap = 1861.80;
  const nH = nhima != null && nhima !== "" ? Number(nhima) : Math.round(b * 0.01 * 100) / 100;
  // Iterate: gross depends on PAYE(gross) and NAPSA(gross)
  let gross = b;
  for (let i = 0; i < 30; i += 1) {
    const napsaNotional = Math.min(gross * 0.05, napsaCap);
    const payeCalc = zmwPaye(gross);
    const payeIter = paye != null && paye !== "" ? Number(paye) : payeCalc;
    const napsaIter = napsa != null && napsa !== "" ? Number(napsa) : napsaNotional;
    const next = b + payeIter + napsaIter + nH;
    if (Math.abs(next - gross) < 0.005) { gross = next; break; }
    gross = next;
  }
  gross = Math.round(gross * 100) / 100;
  const p = paye != null && paye !== "" ? Number(paye) : Math.round(zmwPaye(gross) * 100) / 100;
  let nA;
  if (napsa != null && napsa !== "") {
    nA = Number(napsa);
  } else if (exemptFromNapsa) {
    nA = 0;
    // Recompute gross without NAPSA leg so Net still equals basic
    gross = Math.round((b + p + nH) * 100) / 100;
  } else {
    nA = Math.round(Math.min(gross * 0.05, napsaCap) * 100) / 100;
  }
  const allow = Math.round((Number(allowances) || 0) * 100) / 100;
  if (allow) gross = Math.round((gross + allow) * 100) / 100;
  const taxable = Math.round((gross - nA) * 100) / 100;
  const netAfterStat = Math.round((gross - p - nA - nH) * 100) / 100;  // = b
  const netPay = Math.round((netAfterStat - adv) * 100) / 100;
  const grat = Math.round(netPay * gratuityPct * 100) / 100;
  return {
    basic_zmw: b, gross_zmw: gross, taxable_gross_zmw: taxable,
    napsa_zmw: nA, nhima_zmw: nH, paye_zmw: p,
    salary_advance_zmw: adv,
    net_pay_zmw: netPay,
    gratuity_25_zmw: grat,
    net_with_gratuity_zmw: Math.round((netPay + grat) * 100) / 100,
  };
}

function MONTHS() { return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]; }
function prettyMonth(v) {
  if (!v) return "";
  const [y, m] = v.split("-");
  return `${MONTHS()[parseInt(m, 10) - 1]} ${y}`;
}
function nextCycleMonth() {
  const now = new Date();
  const day = now.getDate();
  // Cycle labeled by the month it CLOSES in (26th). If today ≥ 27th, current cycle ends next month.
  let y = now.getFullYear(), m = now.getMonth() + 1; // 1-based
  if (day >= 27) { m += 1; if (m > 12) { m = 1; y += 1; } }
  return `${y}-${String(m).padStart(2, "0")}`;
}

export default function Payroll() {
  const { user } = useAuth();
  const isPayrollAdmin = user.role === "admin" || user.role === "finance";
  const canDownload = canSeeSalary(user.role);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);

  // Run-cycle modal
  const [runOpen, setRunOpen] = useState(false);
  const [runMonth, setRunMonth] = useState(nextCycleMonth());
  const [defaultHours, setDefaultHours] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [running, setRunning] = useState(false);

  // Edit modal
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);

  async function loadSummary() {
    try {
      const { data } = await api.get("/payroll/summary");
      setSummary(data);
      if (data.months?.length) {
        const latest = data.months[data.months.length - 1].month_key;
        setMonth((m) => m || latest);
      }
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { loadSummary(); }, []);

  useEffect(() => {
    if (!month) return;
    (async () => {
      try { const { data } = await api.get(`/payroll?month=${month}`); setRows(data || []); }
      catch (e) { toast.error(formatError(e)); }
    })();
  }, [month]);

  async function runCycle() {
    setRunning(true);
    try {
      const payload = {
        month_key: runMonth,
        overwrite,
      };
      if (defaultHours !== "") payload.default_hours_if_missing = Number(defaultHours);
      const { data } = await api.post("/payroll/run-cycle", payload);
      toast.success(`Cycle ${data.cycle_label}: ${data.created} created, ${data.updated} updated, ${data.skipped} skipped`);
      setRunOpen(false); setMonth(runMonth); loadSummary();
      const { data: fresh } = await api.get(`/payroll?month=${runMonth}`);
      setRows(fresh || []);
    } catch (e) { toast.error(formatError(e)); }
    finally { setRunning(false); }
  }

  async function recalcAllDrafts() {
    if (!window.confirm(`Re-run the grossed-up statutory math on every draft record for ${month}? Basic pay is unchanged; PAYE/NAPSA/NHIMA/gratuity are refreshed.`)) return;
    try {
      const { data } = await api.post(`/payroll/recalc-drafts?month=${month}`);
      toast.success(`Recomputed ${data.updated} draft records`);
      const { data: fresh } = await api.get(`/payroll?month=${month}`);
      setRows(fresh || []);
      loadSummary();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function saveEdit() {
    if (!editRow) return;
    setSaving(true);
    try {
      const patch = { ...editRow };
      // Force a fresh auto-math using current inputs so the backend receives
      // consistent values matching what the user is seeing.
      const computed = autoPayrollMath(patch.basic_zmw, {
        salaryAdvance: patch.salary_advance_zmw,
      });
      patch.gross_zmw = computed.gross_zmw;
      patch.taxable_gross_zmw = computed.taxable_gross_zmw;
      patch.napsa_zmw = Number(patch.napsa_zmw ?? computed.napsa_zmw);
      patch.nhima_zmw = Number(patch.nhima_zmw ?? computed.nhima_zmw);
      patch.paye_zmw = Number(patch.paye_zmw ?? computed.paye_zmw);
      patch.net_pay_zmw = computed.net_pay_zmw;
      patch.gratuity_25_zmw = computed.gratuity_25_zmw;
      patch.net_with_gratuity_zmw = computed.net_with_gratuity_zmw;
      await api.put(`/payroll/${editRow.id}`, patch);
      toast.success("Row updated");
      setEditRow(null);
      const { data } = await api.get(`/payroll?month=${month}`); setRows(data || []);
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  }

  async function recalcRow(rid) {
    try {
      await api.post(`/payroll/${rid}/recalc`);
      toast.success("Recomputed from live daily hours");
      const { data } = await api.get(`/payroll?month=${month}`); setRows(data || []);
    } catch (e) { toast.error(formatError(e)); }
  }

  async function finalizeRow(rid) {
    try {
      await api.post(`/payroll/${rid}/finalize`);
      toast.success("Finalized");
      const { data } = await api.get(`/payroll?month=${month}`); setRows(data || []);
    } catch (e) { toast.error(formatError(e)); }
  }

  async function deleteRow(row) {
    if (!window.confirm(`Delete payroll record for ${row.employee_name || row.employee_code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/payroll/${row.id}`);
      toast.success("Deleted");
      const { data } = await api.get(`/payroll?month=${month}`); setRows(data || []);
      loadSummary();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function bulkDownload() {
    if (!month) return;
    await openBlobPdf(`/payroll-bulk-payslips?month=${month}`);
  }

  const chartData = useMemo(() =>
    (summary?.months || []).map((m) => ({
      label: m.month_key,
      gross: typeof m.total_gross_zmw === "string" ? 0 : m.total_gross_zmw,
      net: typeof m.total_net_zmw === "string" ? 0 : m.total_net_zmw,
      paye: typeof m.total_paye_zmw === "string" ? 0 : m.total_paye_zmw,
    })), [summary]);

  if (loading) return <div className="phizo-label">Loading payroll…</div>;
  const latest = summary?.months?.[summary.months.length - 1] || {};

  return (
    <div data-testid="payroll-page">
      <PageHeader
        title="Payroll"
        subtitle="Cycle 27th → 26th · statutory deductions · draft & finalize workflow"
        actions={
          <>
            <BtnGhost
              onClick={() => rows.length && exportCSV(rows, `payroll-${month}.csv`)}
              data-testid="payroll-export"
            >
              <DownloadSimple size={12} weight="bold" /> CSV
            </BtnGhost>
            {canDownload && rows.length > 0 && (
              <BtnSecondary onClick={bulkDownload} data-testid="payroll-bulk-pdf">
                <Files size={14} weight="bold" className="inline mr-1.5" /> Bulk Payslips
              </BtnSecondary>
            )}
            {isPayrollAdmin && (
              <BtnSecondary onClick={recalcAllDrafts} data-testid="payroll-recalc-drafts">
                <ArrowsClockwise size={14} weight="bold" className="inline mr-1.5" /> Recompute Drafts
              </BtnSecondary>
            )}
            {isPayrollAdmin && (
              <BtnPrimary onClick={() => setRunOpen(true)} data-testid="payroll-run">
                <Play size={14} weight="bold" className="inline mr-1.5" /> Run Payroll
              </BtnPrimary>
            )}
          </>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-6">
        <MetricCard testId="pay-kpi-employees" label="Payroll size (latest)" value={latest.employees || 0} accent="primary" />
        <MetricCard testId="pay-kpi-gross" label="Monthly gross" value={fmtZMW(latest.total_gross_zmw)} accent="default" />
        <MetricCard testId="pay-kpi-net" label="Net pay + gratuity" value={fmtZMW(latest.total_net_with_gratuity_zmw)} accent="success" />
        <MetricCard testId="pay-kpi-paye" label="PAYE (tax)" value={fmtZMW(latest.total_paye_zmw)} accent="warning" />
      </div>

      {chartData.length > 1 && (
        <div className="phizo-card p-5 mb-6">
          <div className="phizo-label mb-3">Monthly Wage Bill Trend</div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis fontSize={11} tickFormatter={(v) => `K ${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtZMW(v)} />
                <Line type="monotone" dataKey="gross" stroke="#002FA7" strokeWidth={2} dot />
                <Line type="monotone" dataKey="net" stroke="#00A142" strokeWidth={2} dot />
                <Line type="monotone" dataKey="paye" stroke="#FF2A2A" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="phizo-card">
        <div className="p-4 border-b border-[#e4e4e7] flex items-center justify-between">
          <div>
            <div className="phizo-label">Payroll Register</div>
            <div className="text-xs text-[#a1a1aa]">
              {prettyMonth(month)} — {rows.length} record{rows.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="phizo-label text-[10px]">Month</div>
            <Select
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="payroll-month"
              className="w-40"
            >
              {(summary?.months || []).map((m) => (
                <option key={m.month_key} value={m.month_key}>{m.month_key}</option>
              ))}
            </Select>
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="p-5"><Empty label={`No payroll records for ${month}.`} /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th>Employee</th><th>Status</th>
                  <th className="text-right">Hrs</th>
                  <th className="text-right">Rate</th>
                  <th className="text-right">Basic</th>
                  <th className="text-right">PAYE</th>
                  <th className="text-right">NAPSA</th>
                  <th className="text-right">NHIMA</th>
                  <th className="text-right">Gross</th>
                  <th className="text-right">Net Pay</th>
                  <th className="text-right">+ Gratuity</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`pay-row-${r.employee_code}`}>
                    <td>
                      <div className="font-medium">{r.employee_name}</div>
                      <div className="mono text-xs text-[#a1a1aa]">{r.damstra_id || r.employee_code}</div>
                    </td>
                    <td>
                      {r.status === "draft" ? (
                        <span className="phizo-status" style={{ background: "#FFC000", color: "#09090b", borderColor: "#FFC000" }}>Draft</span>
                      ) : (
                        <span className="phizo-status" style={{ background: "#00C853", color: "#09090b", borderColor: "#00C853" }}>Final</span>
                      )}
                    </td>
                    <td className="text-right mono">{r.hours_worked || 0}</td>
                    <td className="text-right mono">{fmtZMW(r.hourly_rate_zmw)}</td>
                    <td className="text-right mono">{fmtZMW(r.basic_zmw)}</td>
                    <td className="text-right mono text-[#FF2A2A]">{fmtZMW(r.paye_zmw)}</td>
                    <td className="text-right mono">{fmtZMW(r.napsa_zmw)}</td>
                    <td className="text-right mono">{fmtZMW(r.nhima_zmw)}</td>
                    <td className="text-right mono font-semibold">{fmtZMW(r.gross_zmw)}</td>
                    <td className="text-right mono">{fmtZMW(r.net_pay_zmw)}</td>
                    <td className="text-right mono font-semibold text-[#00A142]">{fmtZMW(r.net_with_gratuity_zmw)}</td>
                    <td className="text-right whitespace-nowrap">
                      {isPayrollAdmin && (
                        <BtnGhost onClick={() => recalcRow(r.id)}
                          className="text-[#002FA7]"
                          title="Recompute basic + statutory + gratuity from latest daily hours"
                          data-testid={`pay-recalc-${r.employee_code}`}>
                          <ArrowsClockwise size={12} weight="bold" />
                        </BtnGhost>
                      )}
                      {isPayrollAdmin && (
                        <BtnGhost onClick={() => setEditRow(r)}
                          data-testid={`pay-edit-${r.employee_code}`}>
                          <PencilSimple size={12} weight="bold" />
                        </BtnGhost>
                      )}
                      {isPayrollAdmin && r.status === "draft" && (
                        <BtnGhost onClick={() => finalizeRow(r.id)}
                          className="text-[#00A142]"
                          data-testid={`pay-finalize-${r.employee_code}`}>
                          <CheckCircle size={12} weight="bold" />
                        </BtnGhost>
                      )}
                      {isPayrollAdmin && (
                        <BtnGhost onClick={() => deleteRow(r)}
                          className="text-[#FF2A2A]"
                          title="Delete payroll record (admin/finance only)"
                          data-testid={`pay-delete-${r.employee_code}`}>
                          <Trash size={12} weight="bold" />
                        </BtnGhost>
                      )}
                      {canDownload && (
                        <button
                          type="button"
                          onClick={() => openBlobPdf(`/payroll/${r.id}/payslip`)}
                          data-testid={`payslip-${r.employee_code}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:text-[#09090b] px-1.5"
                        >
                          <Printer size={12} weight="bold" /> PDF
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Run Payroll modal */}
      <Modal open={runOpen} onClose={() => setRunOpen(false)}
        title="Run Payroll Cycle" testId="run-modal">
        <div className="space-y-4">
          <div className="border border-[#FFC000] bg-[#FFF8E1] p-3 text-xs text-[#52525b]">
            The Phizo payroll cycle runs from the <strong>27th of the previous month</strong> to
            the <strong>26th of the closing month</strong>. Records are created as <strong>drafts</strong>
            for you to review and edit before finalizing.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Closing Month *" hint="The month the cycle closes in — used as the payslip label.">
              <Input type="month" value={runMonth}
                data-testid="run-month"
                onChange={(e) => setRunMonth(e.target.value)} />
            </Field>
            <Field label="Default hours (fallback)" hint="Assigned to employees with no daily-hours logged. Leave blank to skip them.">
              <Input type="number" step="1" value={defaultHours}
                data-testid="run-default-hours"
                onChange={(e) => setDefaultHours(e.target.value)}
                placeholder="e.g. 176" />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={overwrite}
              data-testid="run-overwrite"
              onChange={(e) => setOverwrite(e.target.checked)} />
            <span>Overwrite existing draft records for this month (destroys manual edits)</span>
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#e4e4e7]">
            <BtnSecondary onClick={() => setRunOpen(false)}>Cancel</BtnSecondary>
            <BtnPrimary onClick={runCycle} disabled={running} data-testid="run-submit">
              <Play size={12} weight="bold" className="inline mr-1.5" />
              {running ? "Running…" : "Run Cycle"}
            </BtnPrimary>
          </div>
        </div>
      </Modal>

      {/* Edit row modal */}
      <Modal open={!!editRow} onClose={() => setEditRow(null)}
        title={editRow ? `Edit ${editRow.employee_name} · ${prettyMonth(editRow.month_key)}` : ""}
        wide testId="pay-edit-modal">
        {editRow && (() => {
          // Live-compute from current editRow state so the admin sees the impact of every keystroke.
          const preview = autoPayrollMath(editRow.basic_zmw, {
            salaryAdvance: editRow.salary_advance_zmw,
          });
          const hoursDrivenBasic = () => {
            const h = Number(editRow.hours_worked || 0);
            const r = Number(editRow.hourly_rate_zmw || 0);
            return Math.round(h * r * 100) / 100;
          };
          return (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hours Worked">
              <Input type="number" step="0.25" value={editRow.hours_worked || 0}
                data-testid="edit-hours"
                onChange={(e) => setEditRow({ ...editRow, hours_worked: Number(e.target.value) })} />
            </Field>
            <Field label="Hourly Rate (ZMW)">
              <Input type="number" step="0.01" value={editRow.hourly_rate_zmw || 0}
                onChange={(e) => setEditRow({ ...editRow, hourly_rate_zmw: Number(e.target.value) })} />
            </Field>
            <div className="col-span-2 flex items-center justify-between border border-[#e4e4e7] bg-[#f4f4f5] p-3 text-xs">
              <div>
                <div className="phizo-label">Auto Basic from Hours × Rate</div>
                <div className="mono text-[#002FA7] font-bold text-sm">{fmtZMW(hoursDrivenBasic())}</div>
              </div>
              <BtnSecondary onClick={() => setEditRow({ ...editRow, basic_zmw: hoursDrivenBasic() })}
                data-testid="edit-apply-hours-basic">Apply →</BtnSecondary>
            </div>
            <Field label="Basic (ZMW)" hint="Base salary for the cycle — drives every other line.">
              <Input type="number" step="0.01" value={editRow.basic_zmw || 0}
                data-testid="edit-basic"
                onChange={(e) => setEditRow({ ...editRow, basic_zmw: Number(e.target.value) })} />
            </Field>
            <Field label="Salary Advance">
              <Input type="number" step="0.01" value={editRow.salary_advance_zmw || 0}
                data-testid="edit-advance"
                onChange={(e) => setEditRow({ ...editRow, salary_advance_zmw: Number(e.target.value) })} />
            </Field>
            <Field label={`PAYE (auto: ${fmtZMW(preview.paye_zmw)})`} hint="Leave blank to auto-compute via ZRA 2026 bands">
              <Input type="number" step="0.01" value={editRow.paye_zmw ?? ""}
                onChange={(e) => setEditRow({ ...editRow, paye_zmw: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder={String(preview.paye_zmw)} />
            </Field>
            <Field label={`NAPSA (auto: ${fmtZMW(preview.napsa_zmw)})`} hint="5% of basic, capped at K1,221.80">
              <Input type="number" step="0.01" value={editRow.napsa_zmw ?? ""}
                onChange={(e) => setEditRow({ ...editRow, napsa_zmw: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder={String(preview.napsa_zmw)} />
            </Field>
            <Field label={`NHIMA (auto: ${fmtZMW(preview.nhima_zmw)})`} hint="1% of basic">
              <Input type="number" step="0.01" value={editRow.nhima_zmw ?? ""}
                onChange={(e) => setEditRow({ ...editRow, nhima_zmw: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder={String(preview.nhima_zmw)} />
            </Field>
            <div className="col-span-2 border-t border-[#e4e4e7] pt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="edit-preview">
              <div className="border border-[#e4e4e7] p-2">
                <div className="phizo-label">Gross</div>
                <div className="mono font-bold">{fmtZMW(preview.gross_zmw)}</div>
              </div>
              <div className="border border-[#e4e4e7] p-2">
                <div className="phizo-label text-[#FF2A2A]">Total Deductions</div>
                <div className="mono font-bold text-[#FF2A2A]">
                  {fmtZMW(preview.gross_zmw - preview.net_pay_zmw)}
                </div>
              </div>
              <div className="border border-[#e4e4e7] p-2">
                <div className="phizo-label">Net Pay</div>
                <div className="mono font-bold">{fmtZMW(preview.net_pay_zmw)}</div>
              </div>
              <div className="border border-[#00A142] bg-[#F1FBF3] p-2">
                <div className="phizo-label text-[#00A142]">+ Gratuity (25%)</div>
                <div className="mono font-black text-[#00A142]">{fmtZMW(preview.net_with_gratuity_zmw)}</div>
              </div>
            </div>
            <div className="col-span-2 flex justify-between items-center pt-3 border-t border-[#e4e4e7]">
              <div className="text-xs text-[#a1a1aa]">
                All statutory lines and gratuity auto-recompute from the values above.
              </div>
              <div className="flex gap-2">
                <BtnSecondary onClick={() => setEditRow(null)}>Cancel</BtnSecondary>
                <BtnPrimary onClick={saveEdit} disabled={saving} data-testid="edit-save">
                  {saving ? "Saving…" : "Save"}
                </BtnPrimary>
              </div>
            </div>
          </div>
          );
        })()}
      </Modal>
    </div>
  );
}
